import { SignupFormData } from '../types';
import { getNetworkById } from '../config/networks';
import { getTriggerOnboardingEndpoint, getEnvironment } from '../config/endpoints';

interface OnboardingResponse {
  success: boolean;
  message?: string;
  pipelineRunId?: string;
  pipelineUrl?: string;
  environment?: string;
  error?: string;
}

export interface PipelineStatusResponse {
  success: boolean;
  runId?: string;
  environment?: string;
  status?: string;
  state?: string;
  result?: string;
  createdDate?: string;
  finishedDate?: string;
  url?: string;
  isComplete?: boolean;
  isSuccessful?: boolean;
  error?: string;
}

interface OnboardingData {
  businessName: string;
  dnsSubdomain: string;
  ceoFirstName: string;
  ceoLastName: string;
  ceoEmail: string;
  ceoPhone?: string;
  fcaNumber: string;
  companiesHouseNumber?: string;
  companySize?: string;
  networkId?: string;
  networkNames?: string[];
  totangoId?: string;
  stripeId?: string;
  tenantName?: string;
  buildId?: string;
}

/**
 * Generate TotangoID using FCA number + current year (YY format)
 * Example: FCA number 142356 in 2025 becomes 14235625
 */
const generateTotangoId = (fcaNumber: string): string => {
  // Get current year in YY format
  const currentYear = new Date().getFullYear().toString().slice(-2);
  // Clean FCA number (remove any non-digits)
  const cleanFcaNumber = fcaNumber.replace(/\D/g, '');
  return `${cleanFcaNumber}${currentYear}`;
};

/**
 * Get primary network ID from selected networks
 * Returns the first network's ID, or 'N/A' if none selected
 */
const getPrimaryNetworkId = (formData: SignupFormData): string => {
  if (formData.networkIds && formData.networkIds.length > 0) {
    const primaryNetworkId = formData.networkIds[0];
    const network = getNetworkById(primaryNetworkId);
    return network?.networkId || 'N/A';
  }
  return 'N/A';
};

/**
 * Trigger Azure DevOps onboarding pipeline for new customer
 */
export const triggerOnboarding = async (
  formData: SignupFormData,
  recaptchaToken?: string | null,
  formStartTime?: number,
  formSubmitTime?: number,
  browserFeatures?: any,
  honeypotFields?: { website: string; company_url: string; phone2: string; address: string }
): Promise<OnboardingResponse> => {
  // Get environment-specific onboarding endpoint
  const onboardingEndpoint = getTriggerOnboardingEndpoint();
  const environment = getEnvironment().toUpperCase();

  console.log(`[Onboarding] Using ${environment} environment endpoint:`, onboardingEndpoint);

  try {
    // Use manual TotangoID if provided, otherwise generate from FCA number + current year
    const totangoId = formData.totangoId.trim() 
      ? formData.totangoId.trim() 
      : generateTotangoId(formData.fcaNumber);
    
    // Get primary network ID from selected networks
    const networkId = getPrimaryNetworkId(formData);

    // Use network names directly from form data (NOT UUIDs)
    // The MultiNetworkSelectionField provides both networkIds (UUIDs) and networkNames (shortcodes like 'lg', 'jm')
    // We MUST use networkNames for Monday.com compatibility
    const networkNames = formData.networkNames && formData.networkNames.length > 0
      ? formData.networkNames
      : [];

    // Map form data to onboarding payload
    const onboardingData: OnboardingData = {
      businessName: formData.companyName,
      dnsSubdomain: formData.desiredSubdomain,
      ceoFirstName: formData.firstName,
      ceoLastName: formData.lastName,
      ceoEmail: formData.email,
      ceoPhone: formData.phone,
      fcaNumber: formData.fcaNumber,
      companiesHouseNumber: formData.companiesHouseNumber,
      companySize: formData.companySize,
      networkId: networkId,
      networkNames: networkNames,
      totangoId: totangoId,
      stripeId: 'N/A', // Default for now
    };

    console.log('=== Onboarding Data ===');
    console.log('Environment:', environment);
    console.log('TotangoID:', totangoId, formData.totangoId.trim() ? '(manual)' : '(auto-generated from FCA: ' + formData.fcaNumber + ')');
    console.log('Network ID:', networkId, '(from networks:', formData.networkIds + ')');
    console.log('Endpoint:', onboardingEndpoint);

    const response = await fetch(onboardingEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        formData: onboardingData,
        recaptchaToken,
        formStartTime,
        formSubmitTime,
        browserFeatures,
        website: honeypotFields?.website || '',
        company_url: honeypotFields?.company_url || '',
        phone2: honeypotFields?.phone2 || '',
        address: honeypotFields?.address || ''
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result: OnboardingResponse = await response.json();
    return result;

  } catch (error) {
    console.error(`Error triggering ${environment} onboarding:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Check if onboarding is enabled for current environment
 * Now using centralized endpoints configuration, so always enabled
 */
export const isOnboardingEnabled = (): boolean => {
  return true;
};

/**
 * Poll Azure DevOps pipeline status for completion
 */
export const pollPipelineStatus = async (runId: string, environment: 'dev' | 'prod' = 'dev'): Promise<PipelineStatusResponse> => {
  // Determine polling endpoint based on environment
  const isDev = environment === 'dev';
  const pollingEndpoint = isDev 
    ? process.env.REACT_APP_PIPELINE_POLLING_ENDPOINT_DEV
    : process.env.REACT_APP_PIPELINE_POLLING_ENDPOINT;
  
  if (!pollingEndpoint) {
    console.warn(`${environment.toUpperCase()} pipeline polling endpoint not configured`);
    return {
      success: false,
      error: `${environment.toUpperCase()} pipeline polling service not configured`
    };
  }

  try {
    const response = await fetch(pollingEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        runId,
        environment
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    const result: PipelineStatusResponse = await response.json();
    return result;

  } catch (error) {
    console.error(`Error polling ${environment.toUpperCase()} pipeline status:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Poll pipeline status with automatic retry and completion detection
 */
export const waitForPipelineCompletion = async (
  runId: string, 
  environment: 'dev' | 'prod' = 'dev',
  options: {
    maxAttempts?: number;
    intervalMs?: number;
    onProgress?: (status: PipelineStatusResponse) => void;
  } = {}
): Promise<PipelineStatusResponse> => {
  const { maxAttempts = 30, intervalMs = 10000, onProgress } = options; // Poll every 10 seconds for up to 5 minutes
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const status = await pollPipelineStatus(runId, environment);
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress(status);
      }
      
      // If polling failed, return the error
      if (!status.success) {
        return status;
      }
      
      // If pipeline is complete (success or failure), return the final status
      if (status.isComplete) {
        return status;
      }
      
      // If this is the last attempt, return current status
      if (attempt === maxAttempts) {
        return {
          ...status,
          error: 'Pipeline polling timeout - maximum attempts reached'
        };
      }
      
      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, intervalMs));
      
    } catch (error) {
      console.error(`Pipeline polling attempt ${attempt} failed:`, error);
      
      // If this is the last attempt, return the error
      if (attempt === maxAttempts) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Polling failed'
        };
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
  }
  
  // This should never be reached, but just in case
  return {
    success: false,
    error: 'Unexpected end of polling loop'
  };
};
